// src/data/mockData.js
// Enhanced data service with pagination and on-demand loading
import { API_ENDPOINTS, apiClient } from '../config/apiConfig';

// Fetch historical photos with pagination and filtering
export const fetchHistoricalPhotos = async (options = {}) => {
  try {
    const {
      category = '',
      page = 1,
      limit = 20,
      all = false
    } = options;

    console.log('Fetching historical photos with options:', options);
    
    const params = {
      ...(category && category !== 'all' && { category }),
      page: page.toString(),
      limit: limit.toString(),
      ...(all && { all: 'true' })
    };

    const response = await apiClient.get(API_ENDPOINTS.photos.getAll, params);
    console.log('API Response:', response);
    
    // Transform data to ensure consistency
    const transformedData = response.data.map(photo => ({
      id: photo.id,
      year: photo.year,
      title: photo.title,
      publicId: photo.publicId,
      thumbnail: photo.thumbnail,
      fullSize: photo.fullSize || photo.thumbnail,
      description: photo.description,
      category: photo.category,
      tags: photo.tags || [],
      created_at: photo.created_at,
      format: photo.format,
      version: photo.version,
      size: photo.size
    }));
    
    return {
      photos: transformedData,
      pagination: {
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages,
        hasNext: response.hasNext,
        hasPrev: response.hasPrev
      }
    };
  } catch (error) {
    console.error('Error fetching historical photos:', error);
    return {
      photos: [],
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      }
    };
  }
};

// Fetch gallery filters with counts
export const fetchGalleryFilters = async () => {
  try {
    console.log('Fetching gallery filters from backend...');
    const response = await apiClient.get(API_ENDPOINTS.photos.getFilters);
    
    // Backend already includes the filters with counts
    return response.data;
  } catch (error) {
    console.error('Error fetching gallery filters:', error);
    // Return default filters as fallback
    return [
      { id: 'all', label: 'Tutti', count: 0 }
    ];
  }
};

// Fetch only tags/categories
export const fetchTags = async () => {
  try {
    console.log('Fetching tags from backend...');
    const response = await apiClient.get(API_ENDPOINTS.photos.getTags);
    return response.data;
  } catch (error) {
    console.error('Error fetching tags:', error);
    return [];
  }
};

// Cache per le foto caricate per categoria
class PhotoCache {
  constructor() {
    this.cache = new Map();
  }

  getKey(category, page) {
    return `${category}_${page}`;
  }

  get(category, page) {
    return this.cache.get(this.getKey(category, page));
  }

  set(category, page, data) {
    this.cache.set(this.getKey(category, page), data);
  }

  has(category, page) {
    return this.cache.has(this.getKey(category, page));
  }

  clear(category = null) {
    if (category) {
      // Rimuovi solo le entry per una specifica categoria
      for (const key of this.cache.keys()) {
        if (key.startsWith(`${category}_`)) {
          this.cache.delete(key);
        }
      }
    } else {
      // Pulisci tutta la cache
      this.cache.clear();
    }
  }

  getAllPhotosForCategory(category) {
    const photos = [];
    for (const [key, value] of this.cache.entries()) {
      if (key.startsWith(`${category}_`)) {
        photos.push(...value.photos);
      }
    }
    return photos;
  }
}

// Istanza globale della cache
export const photoCache = new PhotoCache();

// Funzione avanzata per caricare foto con cache
export const loadPhotosForCategory = async (category, page = 1, limit = 20) => {
  try {
    // Controlla se abbiamo già questi dati in cache
    if (photoCache.has(category, page)) {
      console.log(`Loading photos from cache for ${category}, page ${page}`);
      return photoCache.get(category, page);
    }

    console.log(`Loading photos from API for ${category}, page ${page}`);
    const result = await fetchHistoricalPhotos({
      category: category === 'all' ? '' : category,
      page,
      limit
    });

    // Salva in cache
    photoCache.set(category, page, result);
    
    return result;
  } catch (error) {
    console.error('Error loading photos for category:', error);
    return {
      photos: [],
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      }
    };
  }
};

// Function to filter photos - now mostly handled by backend but keeping for client-side filtering if needed
export const filterPhotos = (photos, filter) => {
  if (filter === 'all') return photos;
  
  // Filtri per decenni (se necessario lato client)
  if (filter === '90s') return photos.filter(photo => photo.year >= 1990 && photo.year < 2000);
  if (filter === '00s') return photos.filter(photo => photo.year >= 2000 && photo.year < 2010);
  if (filter === '10s') return photos.filter(photo => photo.year >= 2010 && photo.year < 2020);
  if (filter === '20s') return photos.filter(photo => photo.year >= 2020);
  
  // Filtro per categoria
  return photos.filter(photo => photo.category === filter);
};

// Helper per gestire lo stato di caricamento delle categorie
export class CategoryLoadingManager {
  constructor() {
    this.loadingStates = new Map();
    this.loadedCategories = new Set();
  }

  setLoading(category, isLoading) {
    this.loadingStates.set(category, isLoading);
  }

  isLoading(category) {
    return this.loadingStates.get(category) || false;
  }

  setLoaded(category, isLoaded = true) {
    if (isLoaded) {
      this.loadedCategories.add(category);
    } else {
      this.loadedCategories.delete(category);
    }
  }

  isLoaded(category) {
    return this.loadedCategories.has(category);
  }

  reset() {
    this.loadingStates.clear();
    this.loadedCategories.clear();
  }
}

// Istanza globale del manager
export const categoryManager = new CategoryLoadingManager();

// QR code locations - updating to use backend data when available
export const qrCodeLocations = [
  {
    id: "qr1",
    location: "Ingresso principale",
    photoId: 1,
    description: "Questo è l'ingresso storico del Celtic Druid, rimasto praticamente invariato dal 1994."
  },
  {
    id: "qr2",
    location: "Bancone principale",
    photoId: 2,
    description: "Il bancone in legno di quercia, importato direttamente dall'Irlanda, è uno dei simboli del nostro pub."
  },
  {
    id: "qr3",
    location: "Area concerti",
    photoId: 3,
    description: "In questo spazio hanno suonato più di 500 band negli ultimi 30 anni, da gruppi locali a musicisti irlandesi."
  },
  {
    id: "qr4",
    location: "Giardino esterno",
    photoId: 4,
    description: "Quest'area è stata aggiunta nel 2018 e ha trasformato il Celtic Druid in un pub godibile in tutte le stagioni."
  }
];

// Create composite image function - now to be implemented with backend API
export const createCompositeImage = async (historicalPhotoId, userPhotoBlob) => {
  try {
    return createClientSideComposite(historicalPhotoId, userPhotoBlob);
  } catch (error) {
    console.error('Error creating composite image:', error);
    // For now, create a simple composite client-side as fallback
    // This is temporary until you implement the backend endpoint
    return createClientSideComposite(historicalPhotoId, userPhotoBlob);
  }
};

const createClientSideComposite = async (photo, userPhotoBlob) => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1920;

    const loadImage = (src) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

    const [
      historicalImg,
      userImg,
      logoImg,
      arrowImg,
      woodTexture,
      cloverCorner,
      cloverIcon
    ] = await Promise.all([
      loadImage(photo.fullSize),
      loadImage(URL.createObjectURL(userPhotoBlob)),
      loadImage('logo.jpeg'),
      loadImage('Arrow.png'),
      loadImage('textures/wood_texture.jpeg'),
      loadImage('textures/clover_corner.png'),
      loadImage('textures/clover_icon.png')
    ]);

    // Background texture
    ctx.drawImage(woodTexture, 0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(160, 82, 45, 0.25)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Logo
    ctx.save();
    const logoSize = 120;
    ctx.beginPath();
    ctx.roundRect(35, 146, logoSize, logoSize, 12);
    ctx.clip();
    ctx.drawImage(logoImg, 35, 146, logoSize, logoSize);
    ctx.restore();

    ctx.drawImage(cloverIcon, 160, 160, 40, 40);

    // Historical photo
    let oldImgHeight = canvas.height * 0.4;
    let oldImgWidth = (historicalImg.width / historicalImg.height) * oldImgHeight;
    if (oldImgWidth > 1000) {
      oldImgWidth = 1000;
      oldImgHeight = oldImgWidth * (historicalImg.height / historicalImg.width);
    }
    const oldImgX = (canvas.width - oldImgWidth) / 2;
    const oldImgY = canvas.height * 0.15;

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(oldImgX, oldImgY, oldImgWidth, oldImgHeight, 10);
    ctx.clip();
    ctx.drawImage(historicalImg, oldImgX, oldImgY, oldImgWidth, oldImgHeight);
    ctx.restore();

    // User photo
    let newImgHeight = canvas.height * 0.4;
    let newImgWidth = (userImg.width / userImg.height) * newImgHeight;
    if (newImgWidth > 1000) {
      newImgWidth = 1000;
      newImgHeight = newImgWidth * (userImg.height / userImg.width);
    }
    const newImgX = (canvas.width - newImgWidth) / 2;
    const newImgY = canvas.height * 0.55;

    ctx.save();
    ctx.beginPath();
    ctx.roundRect(newImgX, newImgY, newImgWidth, newImgHeight, 10);
    ctx.clip();
    ctx.drawImage(userImg, newImgX, newImgY, newImgWidth, newImgHeight);
    ctx.restore();

    // Freccia tra le due foto
    ctx.drawImage(arrowImg, 750, 900, 221, 247);

    // Font configuration (Medieval style)
    ctx.fillStyle = '#fff8dc';
    ctx.textAlign = 'center';
    ctx.font = '42px "Cinzel Decorative", serif';
    ctx.fillText(`Nel lontano ${photo.year}...`, canvas.width / 2, oldImgY - 30);
    ctx.fillText('Oggi (2025)', canvas.width / 2, newImgY - 30);

    ctx.font = '54px "Cinzel", serif';
    ctx.fillStyle = '#ffe97f';
    ctx.shadowColor = '#000';
    ctx.shadowBlur = 8;
    ctx.fillText('Grazie di questi 30 anni insieme!', canvas.width / 2, 180);
    ctx.shadowBlur = 0;

    // Footer area
    ctx.fillStyle = 'rgba(25, 15, 5, 0.85)';
    ctx.fillRect(0, canvas.height - 280, canvas.width, 280);

    ctx.fillStyle = '#ffffff';
    ctx.font = '36px "MedievalSharp", cursive';
    ctx.fillText('@celticdruidirishpub', canvas.width / 2, canvas.height - 200);
    ctx.font = '28px "MedievalSharp", cursive';
    ctx.fillText('Contattaci o condividi la tua foto con noi!', canvas.width / 2, canvas.height - 160);
    ctx.font = '16px "MedievalSharp", cursive';
    ctx.fillText('Creato da StoryArch.io', canvas.width / 2, canvas.height - 120);

    // Clover corners
    ctx.globalAlpha = 0.25;
    ctx.drawImage(cloverCorner, 20, 20, 100, 100);
    ctx.drawImage(cloverCorner, canvas.width - 120, 20, 100, 100);
    ctx.drawImage(cloverCorner, 20, canvas.height - 120, 100, 100);
    ctx.drawImage(cloverCorner, canvas.width - 120, canvas.height - 120, 100, 100);
    ctx.globalAlpha = 1.0;

    // Output image
    const compositeImageUrl = canvas.toDataURL('image/jpeg', 0.9);
    return {
      id: Date.now(),
      userPhotoId: null,
      createdAt: new Date().toISOString(),
      compositeUrl: compositeImageUrl
    };

  } catch (error) {
    console.error('Error creating client-side composite:', error);
    throw error;
  }
};



// Pub information - keeping this static for now
export const pubInfo = {
  name: "Celtic Druid Irish Pub",
  foundedYear: 1994,
  address: "Via Caduti di Cefalonia 5/C, Bologna, Italy",
  description: "Il Celtic Druid Irish Pub è un pezzo d'Irlanda nel cuore di Bologna. Dal 1994 offriamo birre artigianali, whiskey pregiati e un'atmosfera autentica che trasporta i nostri clienti nelle terre celtiche. La nostra passione per la cultura irlandese si riflette in ogni dettaglio, dalla musica alle decorazioni, fino ai piatti tipici che serviamo.",
  socialLinks: {
    facebook: "https://facebook.com/celticdruid",
    instagram: "https://instagram.com/celticdruid"
  }
};